import React, { useState, useEffect } from 'react';
import { getToken } from '../services/authService';
import { ImageViewer } from '../components/common/ImageViewer';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function OwnerDashboard({ onBack, onRegister, user }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [myDhabas, setMyDhabas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selectedDhaba, setSelectedDhaba] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [resubmitRemarks, setResubmitRemarks] = useState('');
  const [resubmitting, setResubmitting] = useState(false);
  const [showReapprovalWarning, setShowReapprovalWarning] = useState(false);
  const [tier2Changed, setTier2Changed] = useState(false);
  const [showResubmitForm, setShowResubmitForm] = useState(false);
  const [viewerImages, setViewerImages] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(0);

  useEffect(() => {
    fetchMyDhabas();
  }, []);

  const fetchMyDhabas = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/registrations/my-submissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setMyDhabas(data.submissions || []);
      if (data.submissions?.length > 0) {
        const first = data.submissions[0];
        setSelectedDhaba(first);
        // Lazy-load photos for the selected dhaba
        loadDhabaPhotos(first.id);
      }
    } catch (error) {
      console.error('Failed to fetch dhabas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDhabaPhotos = async (dhabaId) => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/registrations/my-submissions/${dhabaId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedDhaba(prev => prev?.id === dhabaId ? { ...prev, photos: data.photos || {}, images: data.images || [] } : prev);
      }
    } catch (e) { /* silent */ }
  };

  const handleUpdate = async (field, value) => {
    setSelectedDhaba(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const token = getToken();
      // Only send editable fields (exclude locked tier 1 fields)
      const LOCKED = new Set(['id', 'display_id', 'name', 'highway', 'highway_type', 'highway_number', 'latitude', 'longitude', 'dhaba_type', 'status', 'submitted_at', 'rejection_reason', 'info_request_remarks', 'owner_resubmit_remarks', 'rating', 'review_count', 'hygiene_score', 'washroom_rating', 'ht_certified_dining', 'ht_certified_washroom', 'ht_certified_dhaba', 'is_verified']);
      const editableData = {};
      for (const [key, value] of Object.entries(selectedDhaba)) {
        if (!LOCKED.has(key)) editableData[key] = value;
      }
      const response = await fetch(`${API_BASE}/api/registrations/${selectedDhaba.id}/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(editableData)
      });
      const data = await response.json();
      if (response.ok && data.success) {
        if (data.requires_reapproval) {
          setMessage('Changes saved. Your dhaba has been sent for re-approval.');
        } else {
          setMessage('Changes saved successfully!');
        }
        setEditMode(false);
        setTier2Changed(false);
        fetchMyDhabas();
      } else {
        setMessage(data.detail || 'Failed to save changes.');
      }
    } catch (error) {
      setMessage('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleResubmit = async () => {
    if (!selectedDhaba) return;
    setResubmitting(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/registrations/${selectedDhaba.id}/resubmit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ remarks: resubmitRemarks }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage('Resubmitted successfully! Awaiting admin review.');
        setResubmitRemarks('');
        fetchMyDhabas();
      } else {
        setMessage(data.detail || 'Resubmit failed');
      }
    } catch (e) {
      setMessage('Resubmit failed. Please try again.');
    } finally {
      setResubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#FEF3C7', color: '#92400E', label: 'Pending Approval' },
      approved: { bg: '#D1FAE5', color: '#065F46', label: 'Approved' },
      rejected: { bg: '#FEE2E2', color: '#991B1B', label: 'Rejected' },
      info_requested: { bg: '#FFFBEB', color: '#92400E', label: 'Info Requested' },
    };
    const s = styles[status] || styles.pending;
    return (
      <span style={{
        padding: '6px 14px',
        borderRadius: '20px',
        background: s.bg,
        color: s.color,
        fontSize: '13px',
        fontWeight: '600'
      }}>
        {s.label}
      </span>
    );
  };

  // Field tier definitions
  const LOCKED_FIELDS = new Set(['name', 'highway', 'latitude', 'longitude', 'dhaba_type']);
  const REAPPROVAL_FIELDS = new Set(['cuisine', 'pure_veg', 'price_range', 'parking', 'washroom', 'ac_dining', 'wifi', 'card_accepted', 'family_friendly', 'play_area', 'ev_charging', 'parking_type', 'images']);

  const handleUpdateWithTier = (field, value) => {
    handleUpdate(field, value);
    if (REAPPROVAL_FIELDS.has(field) && selectedDhaba?.status === 'approved') {
      setTier2Changed(true);
    }
  };

  const handleSaveWithWarning = async () => {
    if (tier2Changed && selectedDhaba?.status === 'approved') {
      setShowReapprovalWarning(true);
      return;
    }
    handleSave();
  };

  const handleConfirmSave = async () => {
    setShowReapprovalWarning(false);
    handleSave();
  };

  const InputField = ({ label, value, field, type = 'text', placeholder, required, disabled, locked, tooltip }) => (
    <div style={{ marginBottom: '16px', position: 'relative' }}>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: locked ? '#9CA3AF' : '#374151', marginBottom: '6px' }}>
        {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
        {locked && <span style={{ fontSize: '11px', color: '#D4AF37', marginLeft: '6px' }} title={tooltip || 'This field is locked. Register a new dhaba to change it.'}>🔒</span>}
        {!locked && REAPPROVAL_FIELDS.has(field) && selectedDhaba?.status === 'approved' && editMode && (
          <span style={{ fontSize: '10px', color: '#F59E0B', marginLeft: '6px' }}>⚠ re-approval</span>
        )}
      </label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => locked ? null : handleUpdateWithTier(field, e.target.value)}
        disabled={!editMode || disabled || locked}
        placeholder={placeholder}
        title={locked ? (tooltip || 'This field is locked. Register a new dhaba to change it.') : ''}
        style={{
          width: '100%', padding: '12px 14px', border: '2px solid #E5E7EB', borderRadius: '10px', fontSize: '15px',
          background: locked ? '#F3F4F6' : (editMode && !disabled ? '#FFFFFF' : '#F9FAFB'),
          color: locked ? '#9CA3AF' : '#1A1A1A', outline: 'none', cursor: locked ? 'not-allowed' : 'auto'
        }}
      />
    </div>
  );

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#FFFFFF'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid #E5E7EB',
            borderTopColor: '#D4AF37',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px auto'
          }} />
          <p style={{ color: '#6B7280' }}>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(180deg, #1A1A1A 0%, #2D2D2D 100%)',
        padding: '16px',
        position: 'sticky',
        top: 0,
        zIndex: 20
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={onBack}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'rgba(212, 175, 55, 0.1)',
              border: '1px solid rgba(212, 175, 55, 0.2)',
              color: '#D4AF37',
              fontSize: '18px',
              cursor: 'pointer'
            }}
          >
            ←
          </button>
          <h1 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#D4AF37',
            fontFamily: "'Manrope', sans-serif"
          }}>
            Owner Dashboard
          </h1>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        padding: '16px',
        background: '#FFFFFF',
        borderBottom: '1px solid #E5E7EB',
        overflowX: 'auto'
      }}>
        {[
          { id: 'profile', label: 'Profile' },
          { id: 'business', label: 'Business' },
          { id: 'details', label: 'Details' },
          { id: 'menu', label: 'Menu' },
          { id: 'orders', label: 'Orders' },
          { id: 'ledger', label: 'Ledger' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 14px',
              borderRadius: '20px',
              border: 'none',
              background: activeTab === tab.id 
                ? 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 100%)' 
                : '#F3F4F6',
              color: activeTab === tab.id ? '#D4AF37' : '#6B7280',
              fontWeight: '600',
              fontSize: '12px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flex: '1 0 auto',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* No Dhaba State */}
      {myDhabas.length === 0 && (
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <span style={{ fontSize: '64px', display: 'block', marginBottom: '16px' }}>🏪</span>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1A1A1A', marginBottom: '8px' }}>
            No Dhaba Registered
          </h2>
          <p style={{ color: '#6B7280', marginBottom: '24px' }}>
            You haven't registered any dhaba yet.
          </p>
          <button
            onClick={onRegister || onBack}
            style={{
              borderRadius: '25px',
              border: 'none',
              background: 'linear-gradient(135deg, #D4AF37 0%, #C5A028 100%)',
              color: '#1A1A1A',
              fontWeight: '600',
              fontSize: '15px',
              cursor: 'pointer'
            }}
          >
            Register Your Dhaba
          </button>
        </div>
      )}

      {/* Content */}
      {selectedDhaba && (
        <div style={{ padding: '16px' }}>
          {/* Status Card */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1A1A1A' }}>
                  {selectedDhaba.name}
                </h2>
                <p style={{ color: '#6B7280', fontSize: '13px' }}>{selectedDhaba.highway}</p>
                {selectedDhaba.display_id && (
                  <p style={{ color: '#D4AF37', fontSize: '12px', fontWeight: '600', fontFamily: 'monospace', marginTop: '2px' }}>{selectedDhaba.display_id}</p>
                )}
              </div>
              {getStatusBadge(selectedDhaba.status)}
            </div>
          </div>

          {/* Info Requested Alert */}
          {selectedDhaba.status === 'info_requested' && (
            <div style={{ background: '#FFFBEB', borderRadius: '14px', padding: '16px', marginBottom: '16px', border: '2px solid #FDE68A' }}>
              <p style={{ fontWeight: '700', color: '#92400E', fontSize: '14px', marginBottom: '8px' }}>Action Required</p>
              <p style={{ color: '#92400E', fontSize: '13px', marginBottom: '12px' }}>
                {selectedDhaba.info_request_remarks || 'Admin has requested additional information.'}
              </p>
              {!showResubmitForm ? (
                <button
                  onClick={() => setShowResubmitForm(true)}
                  style={{ width: '100%', padding: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                  Resubmit for Review
                </button>
              ) : (
                <div>
                  <p style={{ color: '#6B7280', fontSize: '12px', marginBottom: '8px' }}>
                    Update your details above, then add your remarks:
                  </p>
                  <textarea
                    value={resubmitRemarks}
                    onChange={(e) => setResubmitRemarks(e.target.value)}
                    placeholder="Add your remarks (what you've updated/corrected)..."
                    rows={2}
                    style={{ width: '100%', padding: '10px', border: '2px solid #FDE68A', borderRadius: '8px', fontSize: '14px', resize: 'vertical', outline: 'none', background: '#FFFFFF', marginBottom: '10px' }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setShowResubmitForm(false)}
                      style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '2px solid #E5E7EB', background: '#FFF', color: '#6B7280', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
                      Cancel
                    </button>
                    <button onClick={handleResubmit} disabled={resubmitting}
                      style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                      {resubmitting ? 'Resubmitting...' : 'Confirm Resubmit'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Rejected — Resubmit Option */}
          {selectedDhaba.status === 'rejected' && (
            <div style={{ background: '#FEF2F2', borderRadius: '14px', padding: '16px', marginBottom: '16px', border: '2px solid #FECACA' }}>
              <p style={{ fontWeight: '700', color: '#991B1B', fontSize: '14px', marginBottom: '8px' }}>Registration Rejected</p>
              <p style={{ color: '#991B1B', fontSize: '13px', marginBottom: '12px' }}>
                Reason: {selectedDhaba.rejection_reason || 'No reason provided.'}
              </p>
              {!showResubmitForm ? (
                <button
                  onClick={() => setShowResubmitForm(true)}
                  style={{ width: '100%', padding: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                  Resubmit for Approval
                </button>
              ) : (
                <div>
                  <p style={{ color: '#6B7280', fontSize: '12px', marginBottom: '8px' }}>
                    Update your details above, then explain what you've corrected:
                  </p>
                  <textarea
                    value={resubmitRemarks}
                    onChange={(e) => setResubmitRemarks(e.target.value)}
                    placeholder="Explain what you've corrected..."
                    rows={2}
                    style={{ width: '100%', padding: '10px', border: '2px solid #FECACA', borderRadius: '8px', fontSize: '14px', resize: 'vertical', outline: 'none', background: '#FFFFFF', marginBottom: '10px' }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setShowResubmitForm(false)}
                      style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '2px solid #E5E7EB', background: '#FFF', color: '#6B7280', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
                      Cancel
                    </button>
                    <button onClick={handleResubmit} disabled={resubmitting}
                      style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                      {resubmitting ? 'Resubmitting...' : 'Confirm Resubmit'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Edit Toggle — moved to bottom */}

          {/* Re-approval Warning Modal */}
          {showReapprovalWarning && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
              <div style={{ background: '#FFF', borderRadius: '16px', padding: '24px', maxWidth: '340px', width: '100%' }}>
                <p style={{ fontSize: '16px', fontWeight: '700', color: '#1A1A1A', marginBottom: '8px' }}>Re-approval Required</p>
                <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px', lineHeight: '1.5' }}>
                  These changes affect your public listing. Your dhaba will be sent for re-approval and may not be visible to customers until approved.
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => setShowReapprovalWarning(false)}
                    style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '2px solid #E5E7EB', background: '#FFF', color: '#6B7280', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={handleConfirmSave}
                    style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                    Save & Resubmit
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            {/* Profile Tab - Owner Info (Section A - Private) */}
            {activeTab === 'profile' && (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '20px',
                  paddingBottom: '16px',
                  borderBottom: '1px solid #E5E7EB'
                }}>
                  <span style={{ 
                    padding: '6px 12px', 
                    background: '#FEE2E2', 
                    borderRadius: '8px', 
                    fontSize: '12px',
                    color: '#991B1B',
                    fontWeight: '500'
                  }}>
                    🔒 Private - Only visible to you
                  </span>
                </div>
                
                <InputField
                  label="Owner Name"
                  value={selectedDhaba.owner_name}
                  field="owner_name"
                  placeholder="Full name of owner"
                  required
                />
                
                <InputField
                  label="Owner Phone"
                  value={selectedDhaba.owner_phone}
                  field="owner_phone"
                  type="tel"
                  placeholder="Your phone number"
                  required
                />
                
                <InputField
                  label="Owner Email"
                  value={selectedDhaba.owner_email}
                  field="owner_email"
                  type="email"
                  placeholder="email@example.com"
                />
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    color: '#374151', 
                    marginBottom: '6px' 
                  }}>
                    Registered Business Address
                  </label>
                  <textarea
                    value={selectedDhaba.registered_address || ''}
                    onChange={(e) => handleUpdate('registered_address', e.target.value)}
                    disabled={!editMode}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '10px',
                      fontSize: '15px',
                      background: editMode ? '#FFFFFF' : '#F9FAFB',
                      resize: 'vertical'
                    }}
                    placeholder="Registered address for business"
                  />
                </div>
                
                <InputField
                  label="GSTN (GST Number)"
                  value={selectedDhaba.gstn}
                  field="gstn"
                  placeholder="22AAAAA0000A1Z5"
                />
              </div>
            )}

            {/* Business Info Tab - Section B (Public-facing) */}
            {activeTab === 'business' && (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '20px',
                  paddingBottom: '16px',
                  borderBottom: '1px solid #E5E7EB'
                }}>
                  <span style={{ 
                    padding: '6px 12px', 
                    background: '#D1FAE5', 
                    borderRadius: '8px', 
                    fontSize: '12px',
                    color: '#065F46',
                    fontWeight: '500'
                  }}>
                    👁️ Public - Visible to customers
                  </span>
                </div>
                
                <InputField
                  label="Dhaba Name"
                  value={selectedDhaba.name}
                  field="name"
                  placeholder="Your dhaba name"
                  required
                  locked
                  tooltip="Dhaba name cannot be changed. Register a new dhaba instead."
                />
                
                <InputField
                  label="Highway"
                  value={selectedDhaba.highway}
                  field="highway"
                  placeholder="e.g., NH-48"
                  required
                  locked
                  tooltip="Highway cannot be changed. Register a new dhaba instead."
                />
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    color: '#374151', 
                    marginBottom: '6px' 
                  }}>
                    Address <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <textarea
                    value={selectedDhaba.address || ''}
                    onChange={(e) => handleUpdate('address', e.target.value)}
                    disabled={!editMode}
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '10px',
                      fontSize: '15px',
                      background: editMode ? '#FFFFFF' : '#F9FAFB',
                      resize: 'vertical'
                    }}
                    placeholder="Full address visible to customers"
                  />
                </div>
                
                <InputField
                  label="Customer Contact Phone"
                  value={selectedDhaba.contact_phone}
                  field="contact_phone"
                  type="tel"
                  placeholder="Phone for customer enquiries"
                />
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <InputField
                    label="Latitude"
                    value={selectedDhaba.latitude}
                    field="latitude"
                    type="number"
                    placeholder="28.6139"
                    locked
                    tooltip="Location cannot be changed. Register a new dhaba instead."
                  />
                  <InputField
                    label="Longitude"
                    value={selectedDhaba.longitude}
                    field="longitude"
                    type="number"
                    placeholder="77.2090"
                    locked
                    tooltip="Location cannot be changed. Register a new dhaba instead."
                  />
                </div>
              </div>
            )}

            {/* Details Tab - Cuisine, Amenities, etc */}
            {activeTab === 'details' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #E5E7EB' }}>
                  <span style={{ padding: '6px 12px', background: '#D1FAE5', borderRadius: '8px', fontSize: '12px', color: '#065F46', fontWeight: '500' }}>
                    Public - Visible to customers
                  </span>
                  {editMode && REAPPROVAL_FIELDS.has('cuisine') && selectedDhaba?.status === 'approved' && (
                    <span style={{ fontSize: '10px', color: '#F59E0B' }}>Changes here require re-approval</span>
                  )}
                </div>
                
                {/* Cuisine */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>
                    Cuisine Types {editMode && <span style={{ fontSize: '10px', color: '#F59E0B' }}>⚠ re-approval</span>}
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {['North Indian', 'South Indian', 'Punjabi', 'Rajasthani', 'Mughlai', 'Chinese', 'Continental', 'Street Food'].map(c => {
                      const selected = (selectedDhaba.cuisine || []).includes(c);
                      return (
                        <button key={c}
                          onClick={() => {
                            if (!editMode) return;
                            const current = selectedDhaba.cuisine || [];
                            const updated = selected ? current.filter(x => x !== c) : [...current, c];
                            handleUpdateWithTier('cuisine', updated);
                          }}
                          style={{
                            padding: '8px 14px', borderRadius: '20px', border: 'none', cursor: editMode ? 'pointer' : 'default',
                            background: selected ? 'linear-gradient(135deg, #1A1A1A, #2D2D2D)' : '#F3F4F6',
                            color: selected ? '#D4AF37' : '#374151', fontSize: '13px', fontWeight: '500',
                            opacity: editMode ? 1 : (selected ? 1 : 0.5),
                          }}>
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Price Range */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    Price Range {editMode && <span style={{ fontSize: '10px', color: '#F59E0B' }}>⚠ re-approval</span>}
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {['₹', '₹₹', '₹₹₹'].map(p => (
                      <button key={p}
                        onClick={() => editMode && handleUpdateWithTier('price_range', p)}
                        style={{
                          flex: 1, padding: '12px', borderRadius: '10px', border: 'none', cursor: editMode ? 'pointer' : 'default',
                          background: selectedDhaba.price_range === p ? 'linear-gradient(135deg, #D4AF37, #C5A028)' : '#F9FAFB',
                          color: selectedDhaba.price_range === p ? '#1A1A1A' : '#6B7280', fontSize: '18px', fontWeight: '700',
                        }}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Timing */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Timing</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: editMode ? 'pointer' : 'default' }}>
                      <input type="checkbox" checked={!!selectedDhaba.open_24x7} disabled={!editMode}
                        onChange={() => editMode && handleUpdate('open_24x7', !selectedDhaba.open_24x7)}
                        style={{ width: '18px', height: '18px', accentColor: '#D4AF37' }} />
                      <span style={{ fontSize: '14px' }}>Open 24x7</span>
                    </label>
                  </div>
                  {!selectedDhaba.open_24x7 && (
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '12px', color: '#6B7280' }}>Open</label>
                        <input type="time" value={selectedDhaba.open_time || ''} disabled={!editMode}
                          onChange={(e) => handleUpdate('open_time', e.target.value)}
                          style={{ width: '100%', padding: '10px', border: '2px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', background: editMode ? '#FFF' : '#F9FAFB' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '12px', color: '#6B7280' }}>Close</label>
                        <input type="time" value={selectedDhaba.close_time || ''} disabled={!editMode}
                          onChange={(e) => handleUpdate('close_time', e.target.value)}
                          style={{ width: '100%', padding: '10px', border: '2px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', background: editMode ? '#FFF' : '#F9FAFB' }} />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Amenities */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>
                    Amenities {editMode && <span style={{ fontSize: '10px', color: '#F59E0B' }}>⚠ re-approval</span>}
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                    {[
                      { key: 'parking', icon: '🅿️', label: 'Parking' },
                      { key: 'washroom', icon: '🚻', label: 'Washroom' },
                      { key: 'ac_dining', icon: '❄️', label: 'AC Dining' },
                      { key: 'wifi', icon: '📶', label: 'WiFi' },
                      { key: 'card_accepted', icon: '💳', label: 'Cards' },
                      { key: 'family_friendly', icon: '👨‍👩‍👧', label: 'Family' },
                      { key: 'play_area', icon: '🎠', label: 'Play Area' },
                      { key: 'ev_charging', icon: '⚡', label: 'EV Charging' },
                    ].map(amenity => (
                      <div key={amenity.key}
                        onClick={() => editMode && handleUpdateWithTier(amenity.key, !selectedDhaba[amenity.key])}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px',
                          background: selectedDhaba[amenity.key] ? 'rgba(34, 197, 94, 0.1)' : '#F9FAFB',
                          borderRadius: '10px', border: selectedDhaba[amenity.key] ? '1px solid #22C55E' : '1px solid #E5E7EB',
                          cursor: editMode ? 'pointer' : 'default',
                        }}>
                        <span>{amenity.icon}</span>
                        <span style={{ fontSize: '13px', color: selectedDhaba[amenity.key] ? '#065F46' : '#9CA3AF', fontWeight: '500' }}>
                          {amenity.label}
                        </span>
                        {selectedDhaba[amenity.key] && <span style={{ marginLeft: 'auto', color: '#22C55E' }}>✓</span>}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Photos — Categorized */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>
                    Photos {editMode && <span style={{ fontSize: '10px', color: '#F59E0B' }}>⚠ re-approval</span>}
                  </label>
                  {[
                    { id: 'exterior', label: 'Exterior / Entrance' },
                    { id: 'parking', label: 'Parking Area' },
                    { id: 'dining', label: 'Dining Area' },
                    { id: 'kitchen', label: 'Kitchen / Cooking Area' },
                    { id: 'food_storage', label: 'Food Display / Storage' },
                    { id: 'menu_board', label: 'Menu Board' },
                    { id: 'washroom', label: 'Washroom' },
                  ].map(cat => {
                    const photos = selectedDhaba.photos || {};
                    const catPhotos = photos[cat.id] || [];
                    if (!editMode && catPhotos.length === 0) return null;
                    return (
                      <div key={cat.id} style={{ marginBottom: '12px', padding: '10px', background: '#F9FAFB', borderRadius: '10px' }}>
                        <p style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', marginBottom: '6px' }}>
                          {cat.label} ({catPhotos.length})
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {catPhotos.map((img, idx) => (
                            <div key={`${cat.id}-${idx}`} style={{ position: 'relative', width: '70px', height: '70px' }}>
                              <img src={img} alt=""
                                onClick={() => { if (!editMode) { setViewerImages(catPhotos); setViewerIndex(idx); } }}
                                style={{ width: '70px', height: '70px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #E5E7EB', cursor: editMode ? 'default' : 'pointer' }} />
                              {editMode && (
                                <button onClick={() => {
                                  const updated = { ...photos, [cat.id]: catPhotos.filter((_, i) => i !== idx) };
                                  handleUpdateWithTier('photos', updated);
                                }}
                                  style={{ position: 'absolute', top: '-5px', right: '-5px', width: '20px', height: '20px', borderRadius: '50%', background: '#EF4444', color: '#FFF', border: '2px solid #FFF', fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                          {editMode && (
                            <label style={{ width: '70px', height: '70px', borderRadius: '8px', border: '2px dashed #D4AF37', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'rgba(212,175,55,0.05)' }}>
                              <span style={{ fontSize: '16px', color: '#D4AF37' }}>+</span>
                              <span style={{ fontSize: '9px', color: '#D4AF37' }}>Add</span>
                              <input type="file" style={{ display: 'none' }}
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  const reader = new FileReader();
                                  reader.onload = () => {
                                    const updated = { ...photos, [cat.id]: [...catPhotos, reader.result] };
                                    handleUpdateWithTier('photos', updated);
                                  };
                                  reader.readAsDataURL(file);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {(() => {
                    const photos = selectedDhaba.photos || {};
                    const total = Object.values(photos).reduce((sum, arr) => sum + (arr?.length || 0), 0);
                    if (total === 0 && !editMode) return <p style={{ fontSize: '12px', color: '#9CA3AF' }}>No photos uploaded</p>;
                    return null;
                  })()}
                </div>

                {/* Tags */}
                {selectedDhaba.tags?.length > 0 && (
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '13px', 
                      fontWeight: '600', 
                      color: '#374151', 
                      marginBottom: '10px' 
                    }}>
                      Special Tags
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {selectedDhaba.tags.map(tag => (
                        <span
                          key={tag}
                          style={{
                            padding: '8px 14px',
                            borderRadius: '20px',
                            background: 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 50%, #D4AF37 100%)',
                            color: '#FFFFFF',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          ⭐ {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Menu Management Tab */}
          {activeTab === 'menu' && (
            <MenuManager dhabaId={selectedDhaba.id} />
          )}

          {activeTab === 'orders' && (
            <OrderManager />
          )}

          {activeTab === 'ledger' && (
            <LedgerManager />
          )}

          {/* Message */}
          {message && (
            <div style={{ padding: '12px 16px', borderRadius: '12px', marginTop: '16px', textAlign: 'center',
              background: message.includes('success') || message.includes('saved') ? '#D1FAE5' : '#FEE2E2',
              color: message.includes('success') || message.includes('saved') ? '#065F46' : '#991B1B',
              fontSize: '14px' }}>
              {message}
            </div>
          )}

          {/* Edit / Save / Cancel — Bottom of page */}
          <div style={{ marginTop: '20px', paddingBottom: '20px' }}>
            {editMode ? (
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => { setEditMode(false); setTier2Changed(false); }}
                  style={{ flex: 1, padding: '14px', borderRadius: '12px', border: '2px solid #E5E7EB', background: '#FFF', color: '#6B7280', fontWeight: '600', fontSize: '15px', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={handleSaveWithWarning} disabled={saving}
                  style={{ flex: 1, padding: '14px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A', fontWeight: '700', fontSize: '15px', cursor: saving ? 'wait' : 'pointer' }}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            ) : (
              <button onClick={() => { setEditMode(true); setTier2Changed(false); }}
                style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '2px solid #D4AF37', background: 'rgba(212,175,55,0.1)', color: '#92700C', fontWeight: '600', fontSize: '15px', cursor: 'pointer' }}>
                Edit Details
              </button>
            )}
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {viewerImages && (
        <ImageViewer images={viewerImages} initialIndex={viewerIndex} onClose={() => setViewerImages(null)} />
      )}
    </div>
  );
}

const MENU_CATEGORIES = ['breakfast', 'starters', 'main', 'breads', 'south_indian', 'chinese', 'beverages', 'desserts', 'other'];

function MenuManager({ dhabaId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', price: '', category: 'main', is_veg: true, is_bestseller: false, prep_time_mins: '15', image: null });

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const fetchMenu = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/registrations/${dhabaId}/menu`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchMenu(); }, [dhabaId]);

  const resetForm = () => {
    setForm({ name: '', description: '', price: '', category: 'main', is_veg: true, is_bestseller: false, prep_time_mins: '15', image: null });
    setEditItem(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.price) return;
    setSaving(true);
    try {
      const token = getToken();
      const url = editItem
        ? `${API_BASE}/api/registrations/${dhabaId}/menu/${editItem.id}`
        : `${API_BASE}/api/registrations/${dhabaId}/menu`;
      const res = await fetch(url, {
        method: editItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, price: parseFloat(form.price), prep_time_mins: parseInt(form.prep_time_mins) || 15 }),
      });
      if (res.ok) { resetForm(); fetchMenu(); }
    } catch (e) { /* silent */ }
    setSaving(false);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this menu item?')) return;
    try {
      const token = getToken();
      await fetch(`${API_BASE}/api/registrations/${dhabaId}/menu/${itemId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchMenu();
    } catch (e) { /* silent */ }
  };

  const handleEdit = (item) => {
    setForm({ name: item.name, description: item.description || '', price: String(item.price), category: item.category || 'main', is_veg: item.is_veg, is_bestseller: item.is_bestseller, prep_time_mins: String(item.prep_time_mins || 15), image: item.image });
    setEditItem(item);
    setShowForm(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm(prev => ({ ...prev, image: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Group items by category
  const grouped = {};
  items.forEach(i => {
    const cat = i.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(i);
  });

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}><p style={{ color: '#6B7280' }}>Loading menu...</p></div>;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{items.length} items</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }}
          style={{ padding: '10px 18px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
          + Add Item
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div style={{ background: '#FFF', borderRadius: '14px', border: '2px solid #D4AF37', padding: '16px', marginBottom: '16px' }}>
          <p style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A', marginBottom: '12px' }}>
            {editItem ? 'Edit Menu Item' : 'Add Menu Item'}
          </p>
          {/* Name */}
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Item name *"
            style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '10px', fontSize: '14px', marginBottom: '8px', outline: 'none' }} />
          {/* Description */}
          <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Description (optional)"
            style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '10px', fontSize: '14px', marginBottom: '8px', outline: 'none' }} />
          {/* Price + Prep Time */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <div style={{ flex: 1 }}>
              <input value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} placeholder="Price (₹) *" type="number"
                style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '10px', fontSize: '14px', outline: 'none' }} />
            </div>
            <div style={{ flex: 1 }}>
              <input value={form.prep_time_mins} onChange={e => setForm(p => ({ ...p, prep_time_mins: e.target.value }))} placeholder="Prep time (mins)" type="number"
                style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '10px', fontSize: '14px', outline: 'none' }} />
            </div>
          </div>
          {/* Category */}
          <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
            style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '10px', fontSize: '14px', marginBottom: '8px', outline: 'none', background: '#FFF' }}>
            {MENU_CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1).replace('_', ' ')}</option>)}
          </select>
          {/* Veg + Bestseller toggles */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
              <input type="checkbox" checked={form.is_veg} onChange={() => setForm(p => ({ ...p, is_veg: !p.is_veg }))}
                style={{ width: '18px', height: '18px', accentColor: '#22C55E' }} />
              <span style={{ color: form.is_veg ? '#22C55E' : '#6B7280' }}>Veg</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
              <input type="checkbox" checked={form.is_bestseller} onChange={() => setForm(p => ({ ...p, is_bestseller: !p.is_bestseller }))}
                style={{ width: '18px', height: '18px', accentColor: '#D4AF37' }} />
              <span style={{ color: form.is_bestseller ? '#D4AF37' : '#6B7280' }}>Bestseller</span>
            </label>
          </div>
          {/* Photo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            {form.image ? (
              <div style={{ position: 'relative' }}>
                <img src={form.image} alt="" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }} />
                <button onClick={() => setForm(p => ({ ...p, image: null }))}
                  style={{ position: 'absolute', top: '-5px', right: '-5px', width: '18px', height: '18px', borderRadius: '50%', background: '#EF4444', color: '#FFF', border: '1px solid #FFF', fontSize: '10px', cursor: 'pointer' }}>✕</button>
              </div>
            ) : (
              <label style={{ width: '60px', height: '60px', borderRadius: '8px', border: '2px dashed #D4AF37', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'rgba(212,175,55,0.05)' }}>
                <span style={{ color: '#D4AF37', fontSize: '20px' }}>+</span>
                <input type="file" style={{ display: 'none' }} onChange={handleImageUpload} />
              </label>
            )}
            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>Item photo (optional)</span>
          </div>
          {/* Save / Cancel */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={resetForm}
              style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '2px solid #E5E7EB', background: '#FFF', color: '#6B7280', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || !form.name.trim() || !form.price}
              style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A', fontWeight: '700', fontSize: '14px', cursor: 'pointer', opacity: (!form.name.trim() || !form.price) ? 0.5 : 1 }}>
              {saving ? 'Saving...' : editItem ? 'Update Item' : 'Add Item'}
            </button>
          </div>
        </div>
      )}

      {/* Menu Items List */}
      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>📋</span>
          <p style={{ fontSize: '16px', fontWeight: '600', color: '#1A1A1A', marginBottom: '4px' }}>No menu items yet</p>
          <p style={{ fontSize: '13px', color: '#6B7280' }}>Add your dishes so customers can pre-order</p>
        </div>
      ) : (
        Object.entries(grouped).map(([cat, catItems]) => (
          <div key={cat} style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '14px', fontWeight: '700', color: '#374151', marginBottom: '8px', textTransform: 'capitalize' }}>
              {cat.replace('_', ' ')} ({catItems.length})
            </p>
            {catItems.map(item => (
              <div key={item.id} style={{ display: 'flex', gap: '10px', padding: '12px', background: '#FFF', borderRadius: '12px', border: '1px solid #E5E7EB', marginBottom: '8px' }}>
                {/* Item info */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                    <span style={{ width: '12px', height: '12px', border: `2px solid ${item.is_veg ? '#22C55E' : '#EF4444'}`, borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: item.is_veg ? '#22C55E' : '#EF4444' }} />
                    </span>
                    {item.is_bestseller && <span style={{ fontSize: '10px', color: '#D4AF37', fontWeight: '600' }}>Bestseller</span>}
                    {!item.is_available && <span style={{ fontSize: '10px', color: '#EF4444', fontWeight: '600' }}>Unavailable</span>}
                  </div>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#1A1A1A' }}>{item.name}</p>
                  <p style={{ fontSize: '14px', fontWeight: '700', color: '#1A1A1A' }}>₹{item.price}</p>
                  {item.description && <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>{item.description}</p>}
                  <p style={{ fontSize: '10px', color: '#9CA3AF' }}>Prep: {item.prep_time_mins} min</p>
                  {/* Edit/Delete */}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                    <button onClick={() => handleEdit(item)} style={{ fontSize: '12px', color: '#D4AF37', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}>Edit</button>
                    <button onClick={() => handleDelete(item.id)} style={{ fontSize: '12px', color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}>Delete</button>
                  </div>
                </div>
                {/* Image */}
                {item.image ? (
                  <img src={item.image} alt="" style={{ width: '70px', height: '70px', objectFit: 'cover', borderRadius: '10px' }} />
                ) : (
                  <div style={{ width: '70px', height: '70px', background: '#F3F4F6', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🍽️</div>
                )}
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

const STATUS_CONFIG = {
  placed: { label: 'New Order', color: '#F59E0B', bg: '#FEF3C7', action: 'Confirm', nextStatus: 'confirmed' },
  confirmed: { label: 'Confirmed', color: '#3B82F6', bg: '#DBEAFE', action: 'Start Preparing', nextStatus: 'preparing' },
  preparing: { label: 'Preparing', color: '#8B5CF6', bg: '#EDE9FE', action: 'Mark Ready', nextStatus: 'ready' },
  ready: { label: 'Ready', color: '#22C55E', bg: '#D1FAE5', action: 'Mark Picked Up', nextStatus: 'picked_up' },
  picked_up: { label: 'Picked Up', color: '#6B7280', bg: '#F3F4F6', action: null, nextStatus: null },
  cancelled: { label: 'Cancelled', color: '#EF4444', bg: '#FEE2E2', action: null, nextStatus: null },
};

function OrderManager() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [updating, setUpdating] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/owner/orders`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setOrders(data.orders || []);
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchOrders(); }, []);

  const handleStatusUpdate = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/owner/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) fetchOrders();
    } catch (e) { /* silent */ }
    setUpdating(null);
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm('Cancel this order?')) return;
    handleStatusUpdate(orderId, 'cancelled');
  };

  const activeOrders = orders.filter(o => ['placed', 'confirmed', 'preparing', 'ready'].includes(o.status));
  const pastOrders = orders.filter(o => ['picked_up', 'cancelled'].includes(o.status));
  const displayOrders = filter === 'active' ? activeOrders : pastOrders;

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}><p style={{ color: '#6B7280' }}>Loading orders...</p></div>;
  }

  return (
    <div>
      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button onClick={() => setFilter('active')}
          style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
            background: filter === 'active' ? '#1A1A1A' : '#F3F4F6', color: filter === 'active' ? '#D4AF37' : '#6B7280' }}>
          Active ({activeOrders.length})
        </button>
        <button onClick={() => setFilter('past')}
          style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
            background: filter === 'past' ? '#1A1A1A' : '#F3F4F6', color: filter === 'past' ? '#D4AF37' : '#6B7280' }}>
          Past ({pastOrders.length})
        </button>
      </div>

      {displayOrders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>{filter === 'active' ? '📦' : '📋'}</span>
          <p style={{ fontSize: '16px', fontWeight: '600', color: '#1A1A1A' }}>No {filter} orders</p>
          <p style={{ fontSize: '13px', color: '#6B7280' }}>{filter === 'active' ? 'New orders will appear here' : 'Completed orders will show here'}</p>
        </div>
      ) : (
        displayOrders.map(order => {
          const config = STATUS_CONFIG[order.status] || STATUS_CONFIG.placed;
          const timeSince = order.placed_at ? Math.round((Date.now() - new Date(order.placed_at).getTime()) / 60000) : 0;
          return (
            <div key={order.id} style={{ background: '#FFF', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '16px', marginBottom: '12px', position: 'relative' }}>
              {/* Header: Status + Time + Menu */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ padding: '4px 10px', borderRadius: '8px', background: config.bg, color: config.color, fontSize: '12px', fontWeight: '700' }}>
                  {config.label}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                    {timeSince < 60 ? `${timeSince}m ago` : `${Math.round(timeSince / 60)}h ago`}
                  </span>
                  {/* Three dot menu */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setMenuOpen(menuOpen === order.id ? null : order.id)}
                      data-testid={`order-menu-${order.id}`}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#6B7280',
                      }}
                    >
                      ⋮
                    </button>
                    {menuOpen === order.id && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        background: '#FFF',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        border: '1px solid #E5E7EB',
                        zIndex: 10,
                        minWidth: '140px',
                        overflow: 'hidden',
                      }}>
                        <button
                          onClick={() => { setSelectedOrder(order); setMenuOpen(null); }}
                          data-testid={`view-details-${order.id}`}
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            border: 'none',
                            background: '#FFF',
                            textAlign: 'left',
                            fontSize: '13px',
                            color: '#374151',
                            cursor: 'pointer',
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Order items */}
              <div style={{ marginBottom: '10px' }}>
                {(order.items || []).map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                    <span style={{ fontSize: '13px', color: '#374151' }}>
                      <span style={{ color: item.is_veg ? '#22C55E' : '#EF4444', marginRight: '4px' }}>●</span>
                      {item.quantity}x {item.name}
                    </span>
                    <span style={{ fontSize: '13px', color: '#374151', fontWeight: '600' }}>₹{item.price * item.quantity}</span>
                  </div>
                ))}
              </div>

              {/* Total + ETA */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #F3F4F6', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '700', color: '#1A1A1A' }}>₹{order.total_amount}</span>
                <span style={{ fontSize: '12px', color: '#6B7280' }}>ETA: {order.eta_minutes} min • Prep: {order.max_prep_mins} min</span>
              </div>

              {/* Special instructions */}
              {order.special_instructions && (
                <div style={{ padding: '8px 10px', background: '#FFFBEB', borderRadius: '8px', marginBottom: '10px', border: '1px solid #FDE68A' }}>
                  <p style={{ fontSize: '11px', color: '#92400E' }}>Note: {order.special_instructions}</p>
                </div>
              )}

              {/* Action button */}
              {config.action && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => handleStatusUpdate(order.id, config.nextStatus)}
                    disabled={updating === order.id}
                    style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none',
                      background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A',
                      fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                    {updating === order.id ? 'Updating...' : config.action}
                  </button>
                  {order.status !== 'ready' && (
                    <button onClick={() => handleCancel(order.id)}
                      style={{ padding: '12px 16px', borderRadius: '10px', border: '2px solid #EF4444',
                        background: '#FFF', color: '#EF4444', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Refresh */}
      <button onClick={fetchOrders}
        style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #E5E7EB', background: '#F9FAFB', color: '#6B7280', fontSize: '13px', cursor: 'pointer', marginTop: '8px' }}>
        Refresh Orders
      </button>

      {/* Order Details Modal - Mobile optimized */}
      {selectedOrder && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
          onClick={() => setSelectedOrder(null)}
        >
          <div
            style={{
              background: '#F9FAFB',
              width: '100%',
              maxWidth: '480px',
              maxHeight: '85vh',
              borderRadius: '20px 20px 0 0',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ 
              background: 'linear-gradient(180deg, #1A1A1A, #2D2D2D)', 
              padding: '16px',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button 
                    onClick={() => setSelectedOrder(null)}
                    data-testid="close-order-modal"
                    style={{ 
                      width: '36px', 
                      height: '36px', 
                      borderRadius: '10px', 
                      background: 'rgba(212,175,55,0.1)', 
                      border: '1px solid rgba(212,175,55,0.2)', 
                      color: '#D4AF37', 
                      fontSize: '16px', 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    ←
                  </button>
                  <h1 style={{ fontSize: '16px', fontWeight: '700', color: '#D4AF37' }}>Order Details</h1>
                </div>
                <span style={{ 
                  padding: '5px 10px', 
                  borderRadius: '8px', 
                  background: (STATUS_CONFIG[selectedOrder.status] || STATUS_CONFIG.placed).bg, 
                  color: (STATUS_CONFIG[selectedOrder.status] || STATUS_CONFIG.placed).color, 
                  fontSize: '11px', 
                  fontWeight: '700' 
                }}>
                  {(STATUS_CONFIG[selectedOrder.status] || STATUS_CONFIG.placed).label}
                </span>
              </div>
            </div>

            {/* Scrollable Content */}
            <div style={{ padding: '16px', overflow: 'auto', flex: 1 }}>
              {/* Order ID + ETA */}
              <div style={{ background: '#FFF', borderRadius: '12px', padding: '14px', marginBottom: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div>
                    <span style={{ fontSize: '11px', color: '#9CA3AF' }}>Order ID</span>
                    <p style={{ fontSize: '13px', fontWeight: '700', color: '#1A1A1A', fontFamily: 'monospace' }}>{selectedOrder.display_id}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: '11px', color: '#9CA3AF' }}>Placed</span>
                    <p style={{ fontSize: '12px', color: '#374151' }}>
                      {selectedOrder.placed_at ? new Date(selectedOrder.placed_at).toLocaleString('en-IN', { 
                        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                      }) : '-'}
                    </p>
                  </div>
                </div>
                {selectedOrder.eta_minutes && (
                  <div style={{ display: 'flex', gap: '20px', paddingTop: '10px', borderTop: '1px solid #F3F4F6' }}>
                    <div>
                      <p style={{ fontSize: '10px', color: '#9CA3AF' }}>ETA</p>
                      <p style={{ fontSize: '14px', fontWeight: '700', color: '#D4AF37' }}>{selectedOrder.eta_minutes} min</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '10px', color: '#9CA3AF' }}>Prep Time</p>
                      <p style={{ fontSize: '14px', fontWeight: '700', color: '#374151' }}>{selectedOrder.max_prep_mins} min</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Ordered Items */}
              <div style={{ background: '#FFF', borderRadius: '12px', padding: '14px', marginBottom: '10px' }}>
                <p style={{ fontSize: '13px', fontWeight: '700', color: '#1A1A1A', marginBottom: '10px' }}>Items ({(selectedOrder.items || []).reduce((sum, i) => sum + i.quantity, 0)})</p>
                {(selectedOrder.items || []).map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: idx < (selectedOrder.items || []).length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ width: '10px', height: '10px', border: `2px solid ${item.is_veg ? '#22C55E' : '#EF4444'}`, borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: item.is_veg ? '#22C55E' : '#EF4444' }} />
                      </span>
                      <div>
                        <p style={{ fontSize: '13px', color: '#1A1A1A' }}>{item.name}</p>
                        <p style={{ fontSize: '11px', color: '#9CA3AF' }}>x{item.quantity}</p>
                      </div>
                    </div>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#1A1A1A' }}>₹{item.price * item.quantity}</p>
                  </div>
                ))}
                {selectedOrder.special_instructions && (
                  <div style={{ marginTop: '8px', padding: '8px', background: '#FFFBEB', borderRadius: '6px', border: '1px solid #FDE68A' }}>
                    <p style={{ fontSize: '10px', color: '#92400E' }}>Note: {selectedOrder.special_instructions}</p>
                  </div>
                )}
              </div>

              {/* Bill Summary */}
              <div style={{ background: '#FFF', borderRadius: '12px', padding: '14px' }}>
                <p style={{ fontSize: '13px', fontWeight: '700', color: '#1A1A1A', marginBottom: '10px' }}>Bill Summary</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>Item Total</span>
                  <span style={{ fontSize: '12px', color: '#1A1A1A' }}>₹{(selectedOrder.item_total || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>GST (5%)</span>
                  <span style={{ fontSize: '12px', color: '#1A1A1A' }}>₹{(selectedOrder.gst_amount || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>Delivery Fee</span>
                  <span style={{ fontSize: '12px', color: '#22C55E' }}>FREE</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>Platform Fee (incl. GST)</span>
                  <span style={{ fontSize: '12px', color: '#1A1A1A' }}>₹{(selectedOrder.platform_fee || 5).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '2px solid #1A1A1A', marginTop: '6px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#1A1A1A' }}>Total Paid</span>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#D4AF37' }}>₹{(selectedOrder.total_amount || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function LedgerManager() {
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [showOrders, setShowOrders] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/owner/ledger?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setLedger(data);
    } catch (e) {
      console.error('Failed to fetch ledger:', e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchLedger(); }, [period]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p style={{ color: '#6B7280' }}>Loading financial data...</p>
      </div>
    );
  }

  if (!ledger) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p style={{ color: '#6B7280' }}>Failed to load ledger data</p>
      </div>
    );
  }

  const { summary, orders } = ledger;

  return (
    <div>
      {/* Period Filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', overflowX: 'auto' }}>
        {[
          { id: 'today', label: 'Today' },
          { id: 'week', label: 'This Week' },
          { id: 'month', label: 'This Month' },
          { id: 'all', label: 'All Time' },
        ].map(p => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            data-testid={`period-${p.id}`}
            style={{
              padding: '8px 14px',
              borderRadius: '20px',
              border: 'none',
              background: period === p.id ? '#1A1A1A' : '#F3F4F6',
              color: period === p.id ? '#D4AF37' : '#6B7280',
              fontWeight: '600',
              fontSize: '12px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 100%)', 
        borderRadius: '16px', 
        padding: '20px', 
        marginBottom: '16px' 
      }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#D4AF37', marginBottom: '16px' }}>
          Earnings Summary
        </h3>
        
        {/* Total Sales */}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>Item Sales</span>
          <span style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: '700' }}>₹{summary.total_item_sales.toLocaleString()}</span>
        </div>
        
        {/* TCS - only deduction from dhaba */}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>TCS (1%)</span>
          <span style={{ color: '#EF4444', fontSize: '14px', fontWeight: '600' }}>-₹{summary.total_tcs.toLocaleString()}</span>
        </div>
        
        {/* Net Payout */}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', marginTop: '8px' }}>
          <span style={{ color: '#D4AF37', fontSize: '16px', fontWeight: '700' }}>Net Payout</span>
          <span style={{ color: '#D4AF37', fontSize: '20px', fontWeight: '800' }}>₹{summary.total_net_payout.toLocaleString()}</span>
        </div>
        
        {/* Info note */}
        <div style={{ 
          marginTop: '12px', 
          padding: '10px', 
          background: 'rgba(255,255,255,0.05)', 
          borderRadius: '8px',
          fontSize: '11px',
          color: 'rgba(255,255,255,0.5)'
        }}>
          Net Payout = Item Sales - TCS (1%)
        </div>
      </div>

      {/* Settlement Status */}
      <div style={{ 
        background: '#FFFFFF', 
        borderRadius: '16px', 
        padding: '20px', 
        marginBottom: '16px',
        border: '1px solid #E5E7EB'
      }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1A1A1A', marginBottom: '16px' }}>
          Settlement Status
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={{ 
            padding: '16px', 
            borderRadius: '12px', 
            background: '#FEF3C7',
            border: '1px solid #FDE68A'
          }}>
            <p style={{ fontSize: '12px', color: '#92400E', fontWeight: '600', marginBottom: '4px' }}>Pending</p>
            <p style={{ fontSize: '20px', fontWeight: '800', color: '#92400E' }}>₹{summary.pending_payout.toLocaleString()}</p>
          </div>
          
          <div style={{ 
            padding: '16px', 
            borderRadius: '12px', 
            background: '#D1FAE5',
            border: '1px solid #A7F3D0'
          }}>
            <p style={{ fontSize: '12px', color: '#065F46', fontWeight: '600', marginBottom: '4px' }}>Settled</p>
            <p style={{ fontSize: '20px', fontWeight: '800', color: '#065F46' }}>₹{summary.settled_payout.toLocaleString()}</p>
          </div>
        </div>
        
        <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '12px', textAlign: 'center' }}>
          Settlements are processed daily for completed orders
        </p>
      </div>

      {/* Order Count */}
      <div style={{ 
        background: '#F9FAFB', 
        borderRadius: '12px', 
        padding: '16px', 
        marginBottom: '16px',
        textAlign: 'center'
      }}>
        <p style={{ fontSize: '32px', fontWeight: '800', color: '#1A1A1A' }}>{summary.order_count}</p>
        <p style={{ fontSize: '13px', color: '#6B7280' }}>Total Orders</p>
      </div>

      {/* Order Details Toggle */}
      {orders.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <button
            onClick={() => setShowOrders(!showOrders)}
            data-testid="toggle-orders-btn"
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              background: '#FFFFFF',
              color: '#374151',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>Order Details ({orders.length})</span>
            <span>{showOrders ? '▲' : '▼'}</span>
          </button>

          {showOrders && (
            <div style={{ marginTop: '12px' }}>
              {orders.map(order => (
                <div
                  key={order.id}
                  style={{
                    background: '#FFFFFF',
                    borderRadius: '12px',
                    padding: '14px',
                    marginBottom: '8px',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#1A1A1A' }}>{order.display_id}</span>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      background: order.settlement_status === 'settled' ? '#D1FAE5' : '#FEF3C7',
                      color: order.settlement_status === 'settled' ? '#065F46' : '#92400E',
                    }}>
                      {order.settlement_status === 'settled' ? 'Settled' : 'Pending'}
                    </span>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '12px' }}>
                    <div>
                      <span style={{ color: '#9CA3AF' }}>Item Total: </span>
                      <span style={{ color: '#374151', fontWeight: '600' }}>₹{order.item_total}</span>
                    </div>
                    <div>
                      <span style={{ color: '#9CA3AF' }}>TCS (1%): </span>
                      <span style={{ color: '#EF4444', fontWeight: '600' }}>-₹{order.tcs_amount}</span>
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginTop: '10px', 
                    paddingTop: '10px',
                    borderTop: '1px solid #F3F4F6'
                  }}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#D4AF37' }}>Net Payout</span>
                    <span style={{ fontSize: '14px', fontWeight: '800', color: '#1A1A1A' }}>₹{order.net_payout}</span>
                  </div>
                  
                  <p style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '8px' }}>
                    {new Date(order.placed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Refresh */}
      <button
        onClick={fetchLedger}
        data-testid="refresh-ledger-btn"
        style={{
          width: '100%',
          padding: '10px',
          borderRadius: '10px',
          border: '1px solid #E5E7EB',
          background: '#F9FAFB',
          color: '#6B7280',
          fontSize: '13px',
          cursor: 'pointer',
        }}
      >
        Refresh Ledger
      </button>
    </div>
  );
}


export default OwnerDashboard;
